import type {
  ConvertedItemLikeParams,
  ConvertedItemParams,
  ConvertedTarget,
} from "../../libs/spec-to-target-converter";
import type { TestTarget, TestTargetType } from "../../types/TestTarget";
import { generateSpecTextFromParams } from "../../libs/spec-hydrator";

/**
 * lib 出力 `ConvertedTarget[]` をアプリ型 `TestTarget[]` に変換する。
 *
 * lib 側で `id` は UUID 採番済みのためそのまま使う (id Map による再採番は行わない)。
 * params を lib スキーマからアプリスキーマ (htmlAttributes / internalProcessFlow /
 * display_text_entries 等) に対応付ける。
 *
 * 最後に、変換後 params から仕様テキスト(specText)/全体仕様(fullSpec)を生成し、
 * `params.poc` へ注入する。これは backend で value 列へ格納され、test-designer が
 * `value.poc.specText` として読む（インポート時に永続化し、消費側の追加操作を不要にする）。
 */
export function toAppTestTargets(converted: ConvertedTarget[]): TestTarget[] {
  const targets = converted.map((t, i) => toAppTestTarget(t, i));
  injectGeneratedSpecText(targets);
  return targets;
}

/**
 * 変換後の各ターゲット params から仕様テキストを生成し、`params.poc.specText` /
 * `params.poc.fullSpec` へ注入する。画面遷移の targetId 名称解決のため全ターゲットを参照表に渡す。
 */
function injectGeneratedSpecText(targets: TestTarget[]): void {
  const targetNameLookup = targets.map((t) => ({
    id: 0,
    name: t.name,
    externalId: t.test_target_id,
  }));

  for (const target of targets) {
    const { specText, fullSpec } = generateSpecTextFromParams({
      targetType: target.type,
      name: target.name,
      description: target.description ?? null,
      params: target.params,
      targetNameLookup,
    });
    if (!specText && !fullSpec) continue;

    const params = { ...((target.params as Record<string, unknown>) ?? {}) };
    const poc = { ...((params.poc as Record<string, unknown>) ?? {}) };
    if (specText) poc.specText = specText;
    if (fullSpec) poc.fullSpec = fullSpec;
    params.poc = poc;
    target.params = params as TestTarget["params"];
  }
}

function toAppTestTarget(t: ConvertedTarget, index: number): TestTarget {
  const rawDescription = "description" in t.params ? t.params.description : "";
  const description = formatImportedDescription(t, rawDescription);
  const params = convertParams(t);
  return {
    id: t.id,
    test_target_id: t.id,
    type: t.type as TestTargetType,
    name: t.name,
    description: description || undefined,
    parentId: t.parentId,
    parent_test_target_id: t.parentId,
    params: params as TestTarget["params"],
    // プレビューで「仕様書パース/マッピング結果のみ」を表示するため、登録用デフォルト注入前の
    // lib params (ConvertedTarget.params) を別フィールドで保持する (登録には使わない)。
    specMappedParams: { ...t.params } as Record<string, unknown>,
    // 仕様書の記述順 (= ConvertedTarget の配列順) を displayOrder に反映。
    // 1 始まりにしているのは、API 側で `displayOrder || 1.0` が 0 を falsy 扱いし
    // 1.0 に上書きしてしまうため (`?? 1.0` ではない既存パスへの defensive)。
    displayOrder: index + 1,
  } as TestTarget;
}

function formatImportedDescription(
  t: ConvertedTarget,
  description: string,
): string {
  if (!isDetailSheetItemLikeTarget(t) || !description) {
    return description;
  }
  if (description.startsWith("# 備考\n")) {
    return description;
  }
  return `# 備考\n${description}`;
}

function isDetailSheetItemLikeTarget(t: ConvertedTarget): boolean {
  if (t.type !== "item" && t.type !== "section" && t.type !== "overlay") {
    return false;
  }
  const sheetName = t.specSource.sheetName;
  return sheetName.startsWith("画面_") || sheetName.startsWith("共通_");
}

function convertParams(t: ConvertedTarget): Record<string, unknown> {
  // インポート由来のターゲットは「未レビュー = 取り込み直後にレビュー待ち」として
  // status を draft 初期化する。UI/利用側で getStatus() フォールバックの "approved" だと
  // レビュー前の自動取り込みが承認済みに見えてしまうため。
  //
  // 各 type で詰める「未指定相当」キー (`null` / `{}` / `false` / `0`) は、UI 手動追加時に
  // form が露出させる初期値と一致させて、import 経由でも同じタブ表示になるようにしている。
  // 既存ターゲット update path では `mergeParamsWithClearSignal` が `null` 値キーを既存値の
  // クリア信号として扱うが、これは「仕様書を真として import で上書き再同期する」運用と整合する。
  const out: Record<string, unknown> = { status: "draft" };
  switch (t.type) {
    case "service":
      // ServiceDetails.base_url は Record<string, string>[] (環境別ドメインの辞書配列) のため、
      // 取り込んだ単一 URL は 1 ブロック ({ default: URL }) に詰める。
      if (t.params.baseUrl) out.base_url = [{ default: t.params.baseUrl }];
      return out;

    case "view": {
      if (t.params.paths.length > 0) {
        out.paths = t.params.paths.map((p) => ({ path: p }));
      }
      if (t.params.conditions && t.params.conditions.length > 0) {
        out.conditions = [...t.params.conditions];
      }
      return out;
    }

    case "component":
      return out;

    case "component-ref": {
      out.referenceTargetId = t.params.referenceTargetId;
      if (t.params.conditions && t.params.conditions.length > 0) {
        out.conditions = [...t.params.conditions];
      }
      return out;
    }

    case "section":
    case "overlay":
      Object.assign(out, { maskType: null, maskTags: null });
      Object.assign(out, convertItemLikeParams(t.params, t.id));
      return out;

    case "item": {
      const p: ConvertedItemParams = t.params;
      // UI 手動 item 追加時に form が露出させる「未指定相当」フィールドを揃える。
      // itemCategory / itemSubCategory はデータの確定値 (= 凡例マッピングで決まる) のため
      // ここではデフォルトを入れず、lib が判定できた場合のみ p.itemCategory / p.itemSubCategory で
      // セットする (UI 手動の初期ドロップダウン値 "Action"/"button" は「ユーザーが選んだ」ものでは
      // ないため、import 側で勝手に断定すると誤った種別を保存してしまう)。
      //
      // 実行制御フラグは camel で書く: `testTargetService.camelToSnake` は本配列 (`isManual` /
      // `failCount` / `isDownload` / `forceAnalyze`) のみ snake へ変換、入力が既に snake なら
      // skip (= drop) する仕様のため、ここで snake で書くと backend へ届かない。
      Object.assign(out, {
        io: null,
        options: { checkbox: {} },
        maskTags: null,
        maskType: null,
        selector: {},
        dbMapping: null,
        isManual: false,
        failCount: 0,
        isTemplate: null,
        isDownload: false,
        formCategory: null,
        customTypeFlg: false,
        forceAnalyze: false,
        htmlAttributes: { required: false },
      });
      Object.assign(out, convertItemLikeParams(p, t.id));
      // convertItemLikeParams は validation 指定時に `htmlAttributes` を新規 object で
      // 上書きするため、デフォルトの `{ required: false }` が浅いマージで消える。
      // 仕様書側で required 未指定 (minLength のみ等) のケースを救うため、最後に defensive merge する。
      out.htmlAttributes = { required: false, ...(out.htmlAttributes as Record<string, unknown>) };
      if (p.itemCategory) out.itemCategory = p.itemCategory;
      if (p.itemSubCategory) out.itemSubCategory = p.itemSubCategory;
      if (p.objectTypes && p.objectTypes.length > 1) {
        out.objectTypes = [...p.objectTypes];
      }
      if (p.itemCategories && p.itemCategories.length > 1) {
        out.itemCategories = p.itemCategories.map((entry) => ({ ...entry }));
      }
      return out;
    }
  }
  // exhaustive check
  const _exhaustive: never = t;
  throw new Error(`Unknown converted target: ${JSON.stringify(_exhaustive)}`);
}

/**
 * Item / Section / Overlay 共通の派生 params をアプリスキーマに変換する。
 *
 * - htmlAttributes.minLength / maxLength は number 型へ変換 (parseInt 失敗時は不採用)
 * - internalProcessFlow.id は `${idForFlow}-b-${i}` 形式で globally unique
 */
function convertItemLikeParams(
  p: ConvertedItemLikeParams,
  idForFlow: string,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (p.displayText) out.text = p.displayText;
  if (p.formCategory) out.formCategory = p.formCategory;
  if (p.conditions && p.conditions.length > 0) out.conditions = [...p.conditions];
  if (p.io) out.io = p.io;
  if (p.isTemplate !== undefined) out.isTemplate = p.isTemplate;
  if (p.dbMapping && p.dbMapping.length > 0) {
    out.dbMapping = p.dbMapping.map((d) => ({ table: d.table, column: d.column }));
  }
  if (p.validation) {
    const ha: Record<string, unknown> = {};
    if (p.validation.required !== undefined) ha.required = p.validation.required;
    if (p.validation.defaultValue) ha.defaultValue = p.validation.defaultValue;
    const minLen = parseLength(p.validation.minLength);
    if (minLen !== undefined) ha.minLength = minLen;
    const maxLen = parseLength(p.validation.maxLength);
    if (maxLen !== undefined) ha.maxLength = maxLen;
    if (p.validation.pattern) ha.pattern = p.validation.pattern;
    if (Object.keys(ha).length > 0) out.htmlAttributes = ha;
  }
  if (p.behaviors && p.behaviors.length > 0) {
    out.internalProcessFlow = p.behaviors.map((b, i) => ({
      id: `${idForFlow}-b-${i}`,
      kind: "step",
      processName: b.summary,
      precondition: b.precondition,
      processContent: b.processContent,
      expectedChangeOnSuccess: b.expectedChangeOnSuccess,
      expectedChangeOnError: b.expectedChangeOnError,
      isAsync: false,
    }));
  }
  if (p.messages && p.messages.length > 0) {
    out.display_text_entries = p.messages.map((m) => ({
      text: m.content,
      conditions: m.condition ? [m.condition] : [],
    }));
  }
  if (p.transitions && p.transitions.length > 0) {
    out.transitions = p.transitions.map((tr) => ({
      targetId: tr.targetId,
      transitionType: tr.transitionType,
      condition: tr.condition,
    }));
  }
  return out;
}

function parseLength(raw: string | undefined): number | undefined {
  if (!raw) return undefined;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) ? n : undefined;
}
